/** @format */

import React from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { ArrowForward, Refresh } from "@mui/icons-material";

import {
    CollectEmptyHint,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionSubtitle,
    CollectSectionTitle,
} from "../styles";
import { colors } from "../../../utility/types/constant";
import {
    IDashboardOverview,
    UrgencyTier,
} from "../../../utility/types/collectTypes";

interface Props {
    overview: IDashboardOverview | null;
    isLoading: boolean;
    onRefresh: () => void;
}

const TIER_COLORS: Record<UrgencyTier, { bg: string; fg: string; label: string }> = {
    critical: { bg: "#fee2e2", fg: "#b91c1c", label: "Critical" },
    high: { bg: "#ffedd5", fg: "#c2410c", label: "High" },
    medium: { bg: "#fef9c3", fg: "#854d0e", label: "Medium" },
    low: { bg: "#dcfce7", fg: "#166534", label: "Low" },
};

function tierForScore(score: number): UrgencyTier {
    if (score >= 70) return "critical";
    if (score >= 50) return "high";
    if (score >= 25) return "medium";
    return "low";
}

function fmtCurrency(amount: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `$${amount.toFixed(0)}`;
    }
}


// Plain-language "what happens next" for a business owner, derived from the
// invoice's recovery state. Keeps the dashboard reassuring rather than technical.
function nextStep(state?: string): { text: string; fg: string } {
    switch (state) {
        case "monitoring":
        case "due_soon":
            return { text: "Reminder scheduled", fg: "#1e40af" };
        case "due_today":
            return { text: "Reminder sending today", fg: "#1e40af" };
        case "overdue":
        case "active_recovery":
            return { text: "Auto-chasing payment", fg: "#c2410c" };
        case "promise_to_pay":
            return { text: "Awaiting promised payment", fg: "#0f7a5b" };
        case "partially_paid":
            return { text: "Chasing remaining balance", fg: "#8b5cf6" };
        case "disputed":
            return { text: "Needs your review", fg: "#b91c1c" };
        case "manual_review":
            return { text: "Flagged for your review", fg: "#b91c1c" };
        case "recovered":
        case "closed":
            return { text: "Recovered", fg: "#166534" };
        default:
            return { text: "Reminder scheduled", fg: "#1e40af" };
    }
}

const UrgentInvoicesPanel: React.FC<Props> = ({ overview, isLoading, onRefresh }) => {
    const navigate = useNavigate();
    const invoices = (overview?.topUrgentInvoices ?? []).slice(0, 5);

    return (
        <CollectSectionCard>
            <CollectSectionHeader>
                <Box>
                    <CollectSectionTitle>Top 5 Priority Invoices</CollectSectionTitle>
                    <CollectSectionSubtitle>
                        The unpaid invoices most worth your attention right now — and what
                        FundsyFi is doing about each one.
                    </CollectSectionSubtitle>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton size="small" onClick={onRefresh}>
                        <Refresh fontSize="small" />
                    </IconButton>
                </Tooltip>
            </CollectSectionHeader>

            {isLoading && !overview ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress sx={{ color: colors.primary }} />
                </Box>
            ) : invoices.length === 0 ? (
                <CollectEmptyState>
                    <CollectEmptyTitle>Nothing urgent — nice work.</CollectEmptyTitle>
                    <CollectEmptyHint>
                        All open invoices are either current or recently sent.
                    </CollectEmptyHint>
                </CollectEmptyState>
            ) : (
                <TableContainer>
                    <Table size="small" sx={{ fontFamily: "'Montserrat', sans-serif" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">
                                    Balance
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">
                                    Days Late
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>What's next</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Urgency</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.map((inv) => {
                                const score = inv.priorityScore ?? 0;
                                const tier = tierForScore(score);
                                const tone = TIER_COLORS[tier];
                                const balance = inv.balance ?? 0;
                                const days = inv.daysOverdue ?? 0;
                                return (
                                    <TableRow key={inv._id} hover>
                                        <TableCell>{inv.docNumber || "—"}</TableCell>
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontSize: "0.85rem",
                                                    fontWeight: 500,
                                                    maxWidth: 200,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {inv.customerDisplayName || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {fmtCurrency(balance, inv.currency || "USD")}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                sx={{
                                                    color: days > 30 ? "#b91c1c" : days > 0 ? "#c2410c" : "#6b7280",
                                                    fontWeight: days > 0 ? 600 : 400,
                                                    fontSize: "0.85rem",
                                                }}
                                            >
                                                {days > 0 ? days : "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontSize: "0.78rem",
                                                    fontWeight: 600,
                                                    color: nextStep(inv.recoveryState).fg,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {nextStep(inv.recoveryState).text}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={`${tone.label} · ${Math.round(score)}`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: tone.bg,
                                                    color: tone.fg,
                                                    fontWeight: 600,
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontSize: "0.7rem",
                                                    height: 22,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Open invoice">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        navigate(`/collect/invoices/${inv._id}`)
                                                    }
                                                >
                                                    <ArrowForward fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </CollectSectionCard>
    );
};

export default UrgentInvoicesPanel;
